import { useState } from 'react'
import type { DateRange } from '@/components/molecules/date-range-picker-field'
import type { ActionModalVariant } from '@/components/molecules/action-modal'

export type ActionModalActionsConfig = 'both' | 'primary' | 'secondary' | 'none'

export function useDesignSystemPage() {
  const [modalOpen, setModalOpen] = useState(false)
  const [actionModalOpen, setActionModalOpen] = useState(false)
  const [actionModalVariant, setActionModalVariant] = useState<
    ActionModalVariant | undefined
  >(undefined)
  const [actionModalActions, setActionModalActions] =
    useState<ActionModalActionsConfig>('both')

  const openActionModal = (
    variant?: ActionModalVariant,
    actions: ActionModalActionsConfig = 'both',
  ) => {
    setActionModalVariant(variant)
    setActionModalActions(actions)
    setActionModalOpen(true)
  }
  // Only flips the open flag — variant/actions are kept so the icon and buttons
  // don't visually change mid close-animation.
  const closeActionModal = () => setActionModalOpen(false)

  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [selectValue, setSelectValue] = useState('')
  const [searchSelectValue, setSearchSelectValue] = useState('')
  const [toastVariant, setToastVariant] = useState<
    'success' | 'error' | 'info' | undefined
  >(undefined)
  const [stepperStep, setStepperStep] = useState(0)
  const [radioValue, setRadioValue] = useState('dog')
  const [checkboxDog, setCheckboxDog] = useState(false)
  const [checkboxCat, setCheckboxCat] = useState(true)
  const [dateValue, setDateValue] = useState<Date | undefined>(undefined)
  const [dateRange, setDateRange] = useState<DateRange>({
    start: undefined,
    end: undefined,
  })
  const [limitedDateValue, setLimitedDateValue] = useState<Date | undefined>(
    undefined,
  )
  const [limitedDateRange, setLimitedDateRange] = useState<DateRange>({
    start: undefined,
    end: undefined,
  })
  const dateMin = new Date()
  const dateMax = new Date(dateMin.getTime() + 30 * 24 * 60 * 60 * 1000)

  const [chipSelected, setChipSelected] = useState(false)
  const [activeFilter, setActiveFilter] = useState('all')
  const [selectableCheckboxA, setSelectableCheckboxA] = useState(false)
  const [selectableCheckboxB, setSelectableCheckboxB] = useState(true)
  const [selectableRadio, setSelectableRadio] = useState('a')

  const [repeatableEntries, setRepeatableEntries] = useState([{ label: '' }])
  const addRepeatable = () => setRepeatableEntries((e) => [...e, { label: '' }])
  const removeRepeatable = (i: number) =>
    setRepeatableEntries((e) => e.filter((_, idx) => idx !== i))
  const updateRepeatable = (i: number, value: string) =>
    setRepeatableEntries((e) =>
      e.map((entry, idx) => (idx === i ? { label: value } : entry)),
    )

  const [textareaValue, setTextareaValue] = useState('')
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const addSelectedFiles = (files: File[]) =>
    setSelectedFiles((current) => [...current, ...files])
  const removeSelectedFile = (index: number) =>
    setSelectedFiles((current) => current.filter((_, i) => i !== index))
  const [hasSignature, setHasSignature] = useState(false)
  const handleSignatureChange = (dataUrl: string | undefined) =>
    setHasSignature(!!dataUrl)

  return {
    textareaValue,
    setTextareaValue,
    selectedFiles,
    addSelectedFiles,
    removeSelectedFile,
    hasSignature,
    handleSignatureChange,
    modalOpen,
    setModalOpen,
    actionModalOpen,
    actionModalVariant,
    actionModalActions,
    openActionModal,
    closeActionModal,
    sidebarOpen,
    setSidebarOpen,
    selectValue,
    setSelectValue,
    searchSelectValue,
    setSearchSelectValue,
    toastVariant,
    setToastVariant,
    stepperStep,
    setStepperStep,
    radioValue,
    setRadioValue,
    checkboxDog,
    setCheckboxDog,
    checkboxCat,
    setCheckboxCat,
    dateValue,
    setDateValue,
    dateRange,
    setDateRange,
    limitedDateValue,
    setLimitedDateValue,
    limitedDateRange,
    setLimitedDateRange,
    dateMin,
    dateMax,
    chipSelected,
    setChipSelected,
    activeFilter,
    setActiveFilter,
    selectableCheckboxA,
    setSelectableCheckboxA,
    selectableCheckboxB,
    setSelectableCheckboxB,
    selectableRadio,
    setSelectableRadio,
    repeatableEntries,
    addRepeatable,
    removeRepeatable,
    updateRepeatable,
  }
}
